import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import StatusChip from "components/StatusChip";
import { FORM_ERROR } from "final-form";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useCallback, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  ApplicationUser,
  ListRequestOfApplicationUserFilter,
  ListResponseOfApplicationUser,
} from "services/KnowfullyAdminClient";
import {
  Severity,
  SnackbarMessageProps,
  UserStatusType,
} from "types/common-types";
import translateError from "utils/translateError";
import GiveAccessToApplicationForm from "./GiveAccessToApplicationForm";

const ApplicationUsers = ({ userId }: { userId: string }) => {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { applicationUserService } = useGlobalContext();
  const [
    applicationUserToDelete,
    setApplicationUserToDelete,
  ] = useState<ApplicationUser | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [createOpen, createOpenActions] = useBoolean(false);

  const DeleteButton = ({ value }: { value: ApplicationUser }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          onClick={() => setApplicationUserToDelete(value)}
          disabled={
            value.userStatusTypeName.toLowerCase() ===
              UserStatusType.Inactive.toLowerCase() ||
            value.applicationAllowGrantingUserAccess === false
          }
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const handleDelete = useCallback(async () => {
    setApplicationUserToDelete(null);
    if (applicationUserToDelete !== null) {
      try {
        await applicationUserService.removeAccessForApplication(
          applicationUserToDelete.organizationUserId,
          applicationUserToDelete.applicationId
        );
        queryClient.invalidateQueries(["applicationUserDetailsList", userId]);
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "applicationUserAccessRemoved" }),
            Severity.Warning
          )
        );
      } catch (err: any) {
        return { [FORM_ERROR]: translateError(err, t) };
      }
    }
  }, [applicationUserToDelete, applicationUserService, queryClient, userId, t]);

  const { data, isFetching } = useStrictQuery<ListResponseOfApplicationUser>(
    ["applicationUserDetailsList", userId],
    () =>
      applicationUserService.getApplicationUsersByOrganizationUserId(
        ListRequestOfApplicationUserFilter.fromJS({
          parameters: { organizationUserId: userId },
          sortField: "application.Name",
        })
      )
  );

  const applicationUsers = useMemo(() => data?.data ?? [], [data?.data]);

  const currentActiveApplicationIds = useMemo(() => {
    return applicationUsers
      .filter(
        (x) =>
          x.userStatusTypeName.toLowerCase() !==
          UserStatusType.Inactive.toLowerCase()
      )
      .map((x) => x.applicationId);
  }, [applicationUsers]);

  const onApplicationUserAccessGiven = () => {
    createOpenActions.setFalse();
    queryClient.invalidateQueries(["applicationUserDetailsList", userId]);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "applicationUserAccessGiven" }))
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "applicationName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "applicationLabel" }),
    },
    {
      accessor: "userStatusTypeName",
      align: "left",
      width: 0,
      type: "node",
      label: t({ id: "statusLabel" }),
      View: StatusChip,
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  const setRemoveAccessForApplicationTitle = () => {
    return `${t({ id: "applicationUserRemoveAccessForApplicationTitle" })} ${
      applicationUserToDelete?.applicationName
    }`;
  };

  return (
    <>
      <Box mb={3}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box textAlign="right">
              <Button
                variant="outlined"
                type="submit"
                color="primary"
                onClick={createOpenActions.setTrue}
              >
                <T id="applicationUserGiveAccessButtonLabel" />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <SortableTable
        {...{
          rows: applicationUsers,
          loading: isFetching,
          dense: true,
          cells,
        }}
      />

      <DeleteEntityDialog
        dialogTitleKey={setRemoveAccessForApplicationTitle()}
        contentKey="applicationUserRemoveAccessForApplicationQuestion"
        confirmLabel="applicationUserRemoveAccessForApplicationButtonLabel"
        open={!!applicationUserToDelete}
        handleDelete={handleDelete}
        onCancel={() => setApplicationUserToDelete(null)}
      />

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <GiveAccessToApplicationForm
          userId={userId}
          onApplicationUserAccessGiven={onApplicationUserAccessGiven}
          onCancel={createOpenActions.setFalse}
          currentApplicationIds={currentActiveApplicationIds}
        />
      </Dialog>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default withErrorBoundary(ApplicationUsers);
